import { Component, EventEmitter, Input, OnInit, Output, ViewChild, TrackByFunction } from '@angular/core'
import { select, Store } from '@ngrx/store'
import {
  Rental,
  SelectableRow,
  isEmptyTable,
  selectRentalSubUnits,
  localeCompareSort,
  setRentalSubUnits,
  ConfirmDialogService,
  UpdateSubunitRentals,
  Toaster,
} from '@tokeet-frontend/tv3-platform'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { SubunitSettingsDialogService } from '../subunit-settings-dialog/subunit-settings-dialog.service'
import { FormGroup, NgModel } from '@angular/forms'

@Component({
  selector: 'app-rental-subunits-table',
  templateUrl: './rental-subunits-table.component.html',
  styleUrls: ['./rental-subunits-table.component.scss'],
})
export class RentalSubunitsTableComponent extends SelectableRow<Rental> implements OnInit {
  @Input('rental') mainRental: Rental
  @Input() settingsForm: FormGroup
  @Output() autoCompactToggle = new EventEmitter<boolean>()

  @ViewChild('paginator', { static: true }) paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) sort: MatSort

  displayedColumns = ['name', 'multi_unit_status', 'multi_unit_priority', 'edit']
  isEmptyTable$ = isEmptyTable(this.dataSource)

  existingPriorities: number[] = []

  trackBy: TrackByFunction<Rental> = (index, item) => item.id

  constructor(
    private store: Store<any>,
    private confirm: ConfirmDialogService,
    private subunitSettingsDialog: SubunitSettingsDialogService,
    private toaster: Toaster
  ) {
    super()
  }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator
    this.dataSource.sort = this.sort
    this.dataSource.sortData = localeCompareSort
    this.store.pipe(select(selectRentalSubUnits(this.mainRental.id))).subscribe((subunits) => {
      this.existingPriorities = subunits
        .filter((s) => !!s.multi_unit_priority)
        .map((subunit) => +subunit.multi_unit_priority)
      this.dataSource.data = subunits
    })
  }

  onSearch(term: string) {
    this.paginator.firstPage()
    this.dataSource.filter = (term + '').trim().toLowerCase()
  }

  onAdd() {
    this.subunitSettingsDialog.open(this.mainRental.id)
  }

  get isMainMultiUnit() {
    return this.mainRental?.multi_unit_id && this.mainRental.multi_unit_id === this.mainRental.id
  }

  get autoCompactEnabled() {
    return !!this.settingsForm?.get('auto_compact')?.value
  }

  onAutoCompactChange(enabled: boolean) {
    this.autoCompactToggle.emit(enabled)
  }

  onEdit(item: Rental) {
    this.subunitSettingsDialog.open(this.mainRental.id, item.id)
  }

  onRentalNameChange(item: Rental, name: string) {
    this.store.dispatch(
      UpdateSubunitRentals({
        items: [
          {
            id: item.id,
            payload: {
              multi_unit_status: item.multi_unit_status,
              multi_unit_priority: item.multi_unit_priority,
              name,
            },
          },
        ],
      })
    )
  }

  onKeyDownPriority($event: KeyboardEvent) {
    // not 0-9 and backspace
    if ($event.key === 'Backspace' || $event.key === 'Delete') return
    if ($event.key < '0' || $event.key > '9') {
      return $event.preventDefault()
    }
  }

  onRentalPriorityChange(item: Rental, priority: number, model: NgModel) {
    if (priority == item.multi_unit_priority) return
    priority = +priority
    if (!priority || priority < 1 || priority > 100) {
      this.toaster.warning($localize`Priority is required, and minimum value is 1, maximum value is 100`)
      model.reset(item.multi_unit_priority)
      return
    }

    if (this.existingPriorities.filter((n) => n != item.multi_unit_priority).includes(priority)) {
      this.toaster.warning($localize`Priority already exists`)
      model.reset(item.multi_unit_priority)
      return
    }

    this.store.dispatch(
      UpdateSubunitRentals({
        items: [
          {
            id: item.id,
            payload: {
              multi_unit_status: item.multi_unit_status,
              multi_unit_priority: priority,
              name: item.name,
            },
          },
        ],
      })
    )
  }

  onStatusChange(item: Rental, enabled: boolean) {
    this.store.dispatch(
      UpdateSubunitRentals({
        items: [
          {
            id: item.id,
            payload: {
              multi_unit_status: enabled ? 1 : 0,
              multi_unit_priority: item.multi_unit_priority,
              name: item.name,
            },
          },
        ],
      })
    )
  }

  onDelete(item: Rental) {
    this.confirm
      .confirm({
        title: $localize`Delete this unit?`,
        body: $localize`Are you sure you want to delete this unit?`,
      })
      .subscribe(() => {
        this.store.dispatch(
          setRentalSubUnits({
            rentalId: this.mainRental.id,
            addedChildren: [],
            removedChildren: [item.id],
          })
        )
      })
  }
}
